<template>
  <div class="fixed inset-0 z-50" role="dialog" aria-modal="true">
    <div class="absolute inset-0 bg-gray-800 bg-opacity-60" @click="close" />

    <div class="absolute inset-0 overflow-y-auto">
      <div class="min-h-full flex items-start justify-center px-4 py-6 sm:px-6 sm:py-10">
        <div class="w-full max-w-5xl max-h-[90vh] rounded-xl bg-white shadow-xl ring-1 ring-black ring-opacity-5 overflow-y-auto overscroll-contain relative">
          <div class="p-6 sm:p-8">
            <div class="flex items-start justify-between">
              <div>
                <h2 class="text-xl sm:text-2xl font-semibold text-gray-900">Pick blocks</h2>
                <p class="mt-1 text-sm text-gray-600">Select one or more blocks to add to your board.</p>
              </div>
              <button
                type="button"
                class="rounded-md p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
                @click="close"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <div class="mt-6 block-gallery-masonry pb-28">
              <button
                v-for="item in items"
                :key="item.id"
                type="button"
                class="block-gallery-item text-left rounded-lg border p-4 transition overflow-hidden focus:outline-none focus:ring-2 focus:ring-green-500"
                :class="selectedIdsSet.has(item.id) ? 'border-green-500 ring-2 ring-green-200 bg-green-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'"
                @click="toggle(item.id)"
              >
                <div class="flex items-start justify-between">
                  <div class="flex items-start min-w-0">
                    <div class="flex-shrink-0 rounded-md bg-gray-100 p-2 text-gray-700">
                      <img
                        v-if="typeof item.icon === 'string' && item.icon"
                        :src="twemojiSrc(item.icon)"
                        class="h-6 w-6"
                        alt=""
                      />
                      <component v-else :is="item.icon" class="h-6 w-6" />
                    </div>
                    <div class="ml-3 min-w-0">
                      <div class="font-medium text-gray-900 truncate">{{ item.title }}</div>
                      <div class="mt-1 text-sm text-gray-600">{{ item.description }}</div>
                      <div v-if="item.blocks && item.blocks.length > 1" class="mt-2 text-xs text-gray-500">
                        Includes {{ item.blocks.length }} blocks
                      </div>
                    </div>
                  </div>

                  <div class="ml-3 pt-1 flex-shrink-0">
                    <div
                      class="h-5 w-5 rounded border"
                      :class="selectedIdsSet.has(item.id) ? 'bg-green-600 border-green-600' : 'bg-white border-gray-300'"
                    />
                  </div>
                </div>

                <div v-if="showPreviews" class="mt-4 rounded-md bg-gray-50 p-3 overflow-hidden">
                  <div
                    class="flex pointer-events-none select-none"
                    :class="(item.blocks && item.blocks.length > 1) ? 'flex-col items-center gap-3' : 'flex-row items-center justify-center'"
                  >
                    <div
                      v-for="(preset, idx) in previewPresets(item)"
                      :key="`${item.id}_${idx}`"
                      class="w-full flex justify-center overflow-hidden"
                    >
                      <AutoFitPreview :padding="12" :min-scale="0.35" :max-scale="1.6" origin="top center">
                        <BlockGalleryBlockPreview
                          :preset="preset"
                          :id-prefix="`gallery_${item.id}`"
                          :index="idx"
                          :size-override="previewSizeOverride"
                        />
                      </AutoFitPreview>
                    </div>
                  </div>
                  <div v-if="item.blocks && item.blocks.length > maxPreviewBlocks" class="mt-2 text-xs text-gray-500 text-center">
                    +{{ item.blocks.length - maxPreviewBlocks }} more
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div class="sticky bottom-0 bg-white border-t border-gray-200 px-6 sm:px-8 py-4 flex items-center justify-between">
              <button
                type="button"
                class="text-sm text-gray-600 hover:text-gray-900"
                @click="clear"
                :disabled="selectedIds.length === 0"
              >
                Clear selection
              </button>

              <div v-if="loading" class="text-sm text-gray-500">Loading gallery…</div>
              <div v-else-if="loadError" class="text-sm text-red-600">Failed to load gallery.</div>

              <div class="flex items-center">
                <button
                  type="button"
                  class="mr-3 inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
                  @click="close"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                  :class="canAdd ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-gray-300 cursor-not-allowed focus:ring-gray-300'"
                  :disabled="!canAdd"
                  @click="add"
                >
                  Add selected
                </button>
              </div>
            </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, h, markRaw } from 'vue';
import AutoFitPreview from './AutoFitPreview.vue';
import BlockGalleryBlockPreview from './BlockGalleryBlockPreview.vue';

const IconClock = markRaw(defineComponent({
  name: 'IconClock',
  setup(_, { attrs }) {
    return () => h(
      'svg',
      {
        ...attrs,
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        fill: 'currentColor',
      },
      [
        h('path', {
          'fill-rule': 'evenodd',
          d: 'M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm.75 5.25a.75.75 0 0 0-1.5 0v4.75c0 .199.079.39.22.53l3.25 3.25a.75.75 0 1 0 1.06-1.06l-3.03-3.03V7.5Z',
          'clip-rule': 'evenodd',
        })
      ]
    );
  },
}));

const IconCounter = markRaw(defineComponent({
  name: 'IconCounter',
  setup(_, { attrs }) {
    return () => h(
      'svg',
      {
        ...attrs,
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        fill: 'currentColor',
      },
      [
        h('path', {
          d: 'M4 5a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V5Zm6 4a1 1 0 0 0-2 0v8a1 1 0 1 0 2 0V9Zm4 2a1 1 0 1 0-2 0v6a1 1 0 1 0 2 0v-6Zm4 4a1 1 0 1 0-2 0v2a1 1 0 1 0 2 0v-2Z',
        })
      ]
    );
  },
}));

const IconBell = markRaw(defineComponent({
  name: 'IconBell',
  setup(_, { attrs }) {
    return () => h(
      'svg',
      {
        ...attrs,
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        fill: 'currentColor',
      },
      [
        h('path', {
          d: 'M12 2a6 6 0 0 0-6 6v3.586l-.707.707A1 1 0 0 0 6 14h12a1 1 0 0 0 .707-1.707L18 11.586V8a6 6 0 0 0-6-6Zm0 20a3 3 0 0 0 3-3H9a3 3 0 0 0 3 3Z',
        })
      ]
    );
  },
}));

const IconPie = markRaw(defineComponent({
  name: 'IconPie',
  setup(_, { attrs }) {
    return () => h(
      'svg',
      {
        ...attrs,
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        fill: 'currentColor',
      },
      [
        h('path', { d: 'M11 2a10 10 0 1 0 10 10H11V2Z' }),
        h('path', { d: 'M13 2.05V11h8.95A10.02 10.02 0 0 0 13 2.05Z' }),
      ]
    );
  },
}));

const IconList = markRaw(defineComponent({
  name: 'IconList',
  setup(_, { attrs }) {
    return () => h(
      'svg',
      {
        ...attrs,
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        fill: 'currentColor',
      },
      [
        h('path', {
          d: 'M7 5a1 1 0 1 0 0 2h14a1 1 0 1 0 0-2H7ZM3 6a1 1 0 1 0 2 0 1 1 0 0 0-2 0ZM7 11a1 1 0 1 0 0 2h14a1 1 0 1 0 0-2H7ZM3 12a1 1 0 1 0 2 0 1 1 0 0 0-2 0ZM7 17a1 1 0 1 0 0 2h14a1 1 0 1 0 0-2H7ZM3 18a1 1 0 1 0 2 0 1 1 0 0 0-2 0Z',
        })
      ]
    );
  },
}));

const IconBookmark = markRaw(defineComponent({
  name: 'IconBookmark',
  setup(_, { attrs }) {
    return () => h(
      'svg',
      {
        ...attrs,
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        fill: 'currentColor',
      },
      [
        h('path', {
          'fill-rule': 'evenodd',
          d: 'M6.32 2.577a49.255 49.255 0 0 1 11.36 0c1.497.174 2.57 1.46 2.57 2.93V21a.75.75 0 0 1-1.085.67L12 18.089l-7.165 3.583A.75.75 0 0 1 3.75 21V5.507c0-1.47 1.073-2.756 2.57-2.93Z',
          'clip-rule': 'evenodd',
        })
      ]
    );
  },
}));

const IconSpark = markRaw(defineComponent({
  name: 'IconSpark',
  setup(_, { attrs }) {
    return () => h(
      'svg',
      {
        ...attrs,
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        fill: 'currentColor',
      },
      [
        h('path', {
          d: 'M12 2l1.2 5.2L18 9l-4.8 1.8L12 16l-1.2-5.2L6 9l4.8-1.8L12 2Z'
        }),
        h('path', {
          d: 'M19 12l.7 3 2.3 1-.2.1-2.1.8L19 20l-.7-3-2.3-1 2.3-1L19 12Z'
        })
      ]
    );
  },
}));

function iconForType(type) {
  switch (type) {
    case 'Block':
      return IconClock;
    case 'BlockCounter':
      return IconCounter;
    case 'BlockCountdown':
      return IconBell;
    case 'BlockProgress':
      return IconPie;
    case 'BlockCollector':
      return IconList;
    case 'BlockLink':
      return IconBookmark;
    default:
      return IconSpark;
  }
}

function normalizeCatalogItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const blocks = Array.isArray(raw.blocks) ? raw.blocks.filter(Boolean) : [];
  const firstType = blocks[0] && blocks[0].type ? String(blocks[0].type) : '';
  const explicitIcon = (typeof raw.icon === 'string' && raw.icon.trim()) ? raw.icon.trim() : '';
  const faceType = blocks[0] && typeof blocks[0].faceType === 'string' ? blocks[0].faceType : '';
  const inferredTwemoji = faceType && /^\d/.test(faceType) ? faceType : '';
  return {
    id: String(raw.id || ''),
    title: String(raw.title || 'Untitled'),
    description: String(raw.description || ''),
    blocks,
    icon: explicitIcon || inferredTwemoji || iconForType(firstType),
  };
}

export default {
  name: 'BlockGalleryModal',
  components: {
    AutoFitPreview,
    BlockGalleryBlockPreview,
  },
  emits: ['close', 'add'],
  props: {
    catalogUrl: {
      type: String,
      default: '/block-gallery.json',
    },
    showPreviews: {
      type: Boolean,
      default: true,
    },
    defaultSelected: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedIds: Array.isArray(this.defaultSelected) ? [...this.defaultSelected] : [],
      items: [],
      loading: false,
      loadError: false,
      maxPreviewBlocks: 3,
      previewSizeOverride: null,
    };
  },
  computed: {
    selectedIdsSet() {
      return new Set(this.selectedIds);
    },
    canAdd() {
      return this.selectedIds.length > 0;
    },
  },
  async mounted() {
    await this.loadCatalog();
  },
  methods: {
    twemojiSrc(code) {
      return `/icons/${code}.svg`;
    },
    async loadCatalog() {
      this.loading = true;
      this.loadError = false;
      try {
        const resp = await fetch(this.catalogUrl, { cache: 'no-cache' });
        if (!resp.ok) throw new Error(`Failed to fetch catalog: ${resp.status}`);
        const json = await resp.json();
        const rawItems = json && Array.isArray(json.items) ? json.items : [];
        const normalized = rawItems
          .map(normalizeCatalogItem)
          .filter(Boolean)
          .filter(i => i.id);

        this.items = normalized;
      } catch (e) {
        this.loadError = true;
        // Keep UX usable even if the catalog fails.
        this.items = [
          {
            id: 'fallback_countdown',
            title: 'Countdown block',
            description: 'Fallback preset (catalog failed to load).',
            blocks: [{ type: 'BlockCountdown', title: 'Countdown', size: 120, color: 'green', faceType: 'default', increaseValue: 1 }],
            icon: '23f1',
          },
        ];
      } finally {
        this.loading = false;
      }
    },
    previewPresets(item) {
      const blocks = item && Array.isArray(item.blocks) ? item.blocks : [];
      return blocks.slice(0, this.maxPreviewBlocks);
    },
    toggle(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) this.selectedIds.push(id);
      else this.selectedIds.splice(idx, 1);
    },
    clear() {
      this.selectedIds.splice(0, this.selectedIds.length);
    },
    close() {
      this.$emit('close');
    },
    add() {
      const picked = this.items.filter(i => this.selectedIdsSet.has(i.id));
      this.$emit('add', picked);
    },
  },
};
</script>

<style scoped>
.block-gallery-masonry {
  column-count: 1;
  column-gap: 16px;
}

@media (min-width: 640px) {
  .block-gallery-masonry {
    column-count: 2;
  }
}

@media (min-width: 1024px) {
  .block-gallery-masonry {
    column-count: 3;
  }
}

.block-gallery-item {
  display: inline-block;
  width: 100%;
  break-inside: avoid;
  -webkit-column-break-inside: avoid;
  margin: 0 0 16px;
}
</style>
